import { useState, useRef, useCallback, useEffect, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Search, CheckCircle2, XCircle, Sparkles, Loader2, MapPin, ShieldAlert,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Country selector types & config                                    */
/* ------------------------------------------------------------------ */

type CountryCode = "se" | "no" | "dk" | "fi";

interface CountryConfig {
  code: CountryCode;
  flag: string;
  labelKey: string;
  placeholders: {
    street: string;
    postcode: string;
    locality: string;
    autocomplete: string;
  };
}

const COUNTRIES: CountryConfig[] = [
  {
    code: "se",
    flag: "🇸🇪",
    labelKey: "productPages.valid.demo.countries.se",
    placeholders: {
      street: "t.ex. Storgatan 1",
      postcode: "t.ex. 111 22",
      locality: "t.ex. Stockholm",
      autocomplete: "Börja skriv en adress…",
    },
  },
  {
    code: "no",
    flag: "🇳🇴",
    labelKey: "productPages.valid.demo.countries.no",
    placeholders: {
      street: "f.eks. Karl Johans gate 1",
      postcode: "f.eks. 0162",
      locality: "f.eks. Oslo",
      autocomplete: "Skriv en adresse…",
    },
  },
  {
    code: "dk",
    flag: "🇩🇰",
    labelKey: "productPages.valid.demo.countries.dk",
    placeholders: {
      street: "f.eks. Strøget 1",
      postcode: "f.eks. 1000",
      locality: "f.eks. København",
      autocomplete: "Skriv en adresse…",
    },
  },
  {
    code: "fi",
    flag: "🇫🇮",
    labelKey: "productPages.valid.demo.countries.fi",
    placeholders: {
      street: "esim. Mannerheimintie 1",
      postcode: "esim. 00100",
      locality: "esim. Helsinki",
      autocomplete: "Kirjoita osoite…",
    },
  },
];

const CountryContext = createContext<{
  country: CountryConfig;
  setCountry: (c: CountryConfig) => void;
}>({
  country: COUNTRIES[0],
  setCountry: () => {},
});

function useCountry() {
  return useContext(CountryContext);
}

/* ------------------------------------------------------------------ */
/*  Rate-limit context  (per action type)                              */
/* ------------------------------------------------------------------ */

interface ActionQuota {
  remaining: number;
  limit: number;
}

interface RateLimitState {
  suggest: ActionQuota;
  validate: ActionQuota;
  enrich: ActionQuota;
  updateQuota: (quotas: Record<string, ActionQuota>) => void;
}

const defaultQuota: ActionQuota = { remaining: 5, limit: 5 };

const RateLimitContext = createContext<RateLimitState>({
  suggest: defaultQuota,
  validate: defaultQuota,
  enrich: defaultQuota,
  updateQuota: () => {},
});

function useRateLimit() {
  return useContext(RateLimitContext);
}

function useActionQuota(action: "suggest" | "validate" | "enrich") {
  const ctx = useRateLimit();
  return {
    ...ctx[action],
    rateLimited: ctx[action].remaining <= 0,
    updateQuota: ctx.updateQuota,
  };
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

async function callValidApi(action: string, params: Record<string, string | number>) {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/valid-api`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ action, params }),
  });

  const data = await response.json();

  if (response.status === 429) {
    throw Object.assign(new Error(data.message || "Rate limit exceeded"), { rateLimited: true, data });
  }
  if (!response.ok) {
    throw new Error(data.error || `API error: ${response.status}`);
  }
  return data;
}

/* ------------------------------------------------------------------ */
/*  Rate-limit banner (shows only when ALL quotas are exhausted)       */
/* ------------------------------------------------------------------ */

function RateLimitBanner() {
  const { suggest, validate, enrich } = useRateLimit();
  const { t } = useTranslation();

  const allExhausted = suggest.remaining <= 0 && validate.remaining <= 0 && enrich.remaining <= 0;

  if (!allExhausted) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-md bg-destructive/10 border border-destructive/30 p-4 flex items-start gap-3"
    >
      <ShieldAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
      <div>
        <p className="font-medium text-destructive text-sm">
          {t("productPages.valid.demo.rateLimit.blocked", "Daglig gräns nådd")}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {t(
            "productPages.valid.demo.rateLimit.blockedDescription",
            "Du har använt alla dina demo-försök för idag. Kom tillbaka imorgon för att testa igen."
          )}
        </p>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Per-tab remaining counter                                          */
/* ------------------------------------------------------------------ */

function TabQuotaCounter({ action, label }: { action: "suggest" | "validate" | "enrich"; label: string }) {
  const quota = useActionQuota(action);
  const { t } = useTranslation();

  if (quota.rateLimited) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-md bg-destructive/10 border border-destructive/30 p-3 flex items-center gap-2 text-sm"
      >
        <ShieldAlert className="h-4 w-4 text-destructive shrink-0" />
        <span className="text-destructive font-medium">
          {t("productPages.valid.demo.rateLimit.tabExhausted", {
            defaultValue: "Inga {{label}} kvar idag.",
            label: label.toLowerCase(),
          })}
        </span>
      </motion.div>
    );
  }

  if (quota.remaining <= 2) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2"
      >
        <ShieldAlert className="h-4 w-4 shrink-0" />
        {t("productPages.valid.demo.rateLimit.tabWarning", {
          defaultValue: "{{remaining}} av {{limit}} {{label}} kvar idag.",
          remaining: quota.remaining,
          limit: quota.limit,
          label: label.toLowerCase(),
        })}
      </motion.div>
    );
  }

  return (
    <p className="text-xs text-muted-foreground">
      {t("productPages.valid.demo.rateLimit.tabCounter", {
        defaultValue: "{{remaining}} av {{limit}} {{label}} kvar idag",
        remaining: quota.remaining,
        limit: quota.limit,
        label: label.toLowerCase(),
      })}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/*  Autocomplete Tab                                                   */
/* ------------------------------------------------------------------ */

function AutocompleteTab() {
  const { t } = useTranslation();
  const { rateLimited, updateQuota } = useActionQuota("suggest");
  const { country } = useCountry();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = useCallback(
    async (q: string) => {
      if (q.length < 2 || rateLimited) {
        setSuggestions([]);
        return;
      }
      setLoading(true);
      try {
        const data = await callValidApi("suggest", { query: q, max_rows: 8, country: country.code });
        setSuggestions(data?.suggestions ?? data?.result ?? []);
        if (data?._remaining) updateQuota(data._remaining);
      } catch (err: any) {
        if (err.rateLimited && err.data) updateQuota({ suggest: { remaining: 0, limit: err.data.limit || 5 } });
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [rateLimited, updateQuota, country.code]
  );

  const handleChange = (value: string) => {
    setQuery(value);
    setSelected(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchSuggestions(value), 300);
  };

  const handleSelect = (suggestion: any) => {
    const parts = [suggestion.street, suggestion.street_number].filter(Boolean).join(" ");
    const label = [parts, suggestion.postcode, suggestion.locality].filter(Boolean).join(", ");
    setQuery(label);
    setSelected(label);
    setSuggestions([]);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("productPages.valid.demo.autocomplete.description")}
      </p>

      <TabQuotaCounter action="suggest" label="Autocomplete-sessioner" />

      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={country.placeholders.autocomplete}
            className="pl-10"
            disabled={rateLimited}
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>

        <AnimatePresence>
          {suggestions.length > 0 && !selected && (
            <motion.ul
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="absolute z-10 mt-1 w-full rounded-md border bg-background shadow-lg max-h-64 overflow-auto"
            >
              {suggestions.map((s, i) => {
                const parts = [s.street, s.street_number].filter(Boolean).join(" ");
                const label = [parts, s.postcode, s.locality].filter(Boolean).join(", ");
                return (
                  <li key={i}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-sm hover:bg-accent transition-colors text-left"
                      onClick={() => handleSelect(s)}
                    >
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span>{label}</span>
                    </button>
                  </li>
                );
              })}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-3 text-sm"
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            <span>
              {t("productPages.valid.demo.autocomplete.selected")}: <strong>{selected}</strong>
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Validate Tab                                                       */
/* ------------------------------------------------------------------ */

function ValidateTab() {
  const { t } = useTranslation();
  const { rateLimited, updateQuota } = useActionQuota("validate");
  const { country } = useCountry();
  const [street, setStreet] = useState("");
  const [postcode, setPostcode] = useState("");
  const [locality, setLocality] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleValidate = async () => {
    if (rateLimited) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await callValidApi("validate", { street, postcode, locality, country: country.code });
      if (data?._remaining) updateQuota(data._remaining);
      setResult(data);
    } catch (err: any) {
      if (err.rateLimited && err.data) updateQuota({ validate: { remaining: 0, limit: err.data.limit || 5 } });
      setResult({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const isValid = result && !result.error && result.is_valid === true;
  const isInvalid = result && !isValid;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("productPages.valid.demo.validate.description")}
      </p>

      <TabQuotaCounter action="validate" label="Valideringar" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label>{t("productPages.valid.demo.validate.streetLabel")}</Label>
          <Input
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder={country.placeholders.street}
            disabled={rateLimited}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("productPages.valid.demo.validate.postcodeLabel")}</Label>
          <Input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder={country.placeholders.postcode}
            disabled={rateLimited}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("productPages.valid.demo.validate.localityLabel")}</Label>
          <Input
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
            placeholder={country.placeholders.locality}
            disabled={rateLimited}
          />
        </div>
      </div>

      <Button onClick={handleValidate} disabled={loading || !street || rateLimited}>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <CheckCircle2 className="h-4 w-4 mr-2" />
        )}
        {t("productPages.valid.demo.validate.button")}
      </Button>

      <AnimatePresence mode="wait">
        {isValid && (
          <motion.div
            key="valid"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-4 space-y-2"
          >
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-medium">
              <CheckCircle2 className="h-5 w-5" />
              {t("productPages.valid.demo.validate.validMessage")}
            </div>
            {result.match?.[0] && (
              <p className="text-sm text-muted-foreground pl-7">
                {result.match[0].street} {result.match[0].street_number}, {result.match[0].postcode}{" "}
                {result.match[0].locality}
              </p>
            )}
          </motion.div>
        )}
        {isInvalid && (
          <motion.div
            key="invalid"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-4 space-y-2"
          >
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-medium">
              <XCircle className="h-5 w-5" />
              {t("productPages.valid.demo.validate.invalidMessage")}
            </div>
            {result.suggestions && result.suggestions.length > 0 && (
              <div className="pl-7 space-y-1">
                <p className="text-xs text-muted-foreground font-medium">
                  {t("productPages.valid.demo.validate.suggestions")}
                </p>
                {result.suggestions.map((s: any, i: number) => (
                  <p key={i} className="text-sm text-muted-foreground">
                    {s.street} {s.street_number}, {s.postcode} {s.locality}
                  </p>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Enrich Tab                                                         */
/* ------------------------------------------------------------------ */

function EnrichTab() {
  const { t } = useTranslation();
  const { rateLimited, updateQuota } = useActionQuota("enrich");
  const { country } = useCountry();
  const [street, setStreet] = useState("");
  const [postcode, setPostcode] = useState("");
  const [locality, setLocality] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleEnrich = async () => {
    if (rateLimited) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await callValidApi("enrich", { street, postcode, locality, country: country.code });
      if (data?._remaining) updateQuota(data._remaining);
      setResult(data);
    } catch (err: any) {
      if (err.rateLimited && err.data) updateQuota({ enrich: { remaining: 0, limit: err.data.limit || 5 } });
      setResult({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const matchItem = Array.isArray(result?.match) ? result.match[0] : result?.match;
  const hasError = result && (!matchItem || result.error);

  const enrichmentRows = matchItem
    ? [
        { label: t("productPages.valid.enrichmentData.items.county.name"), value: matchItem.adm1_name },
        { label: t("productPages.valid.enrichmentData.items.municipality.name"), value: matchItem.adm2_name },
        { label: t("productPages.valid.enrichmentData.items.district.name"), value: matchItem.adm5_name },
        { label: t("productPages.valid.enrichmentData.items.parish.name"), value: matchItem.adm3_name },
        { label: t("productPages.valid.enrichmentData.items.lkfCode.name"), value: matchItem.adm3_code },
        { label: t("productPages.valid.enrichmentData.items.municipalityCode.name"), value: matchItem.adm2_code },
        { label: t("productPages.valid.enrichmentData.items.property.name"), value: matchItem.property_designation },
        {
          label: t("productPages.valid.enrichmentData.items.coordinates.name"),
          value:
            matchItem.latitude && matchItem.longitude
              ? `${matchItem.latitude}, ${matchItem.longitude}`
              : undefined,
        },
        { label: t("productPages.valid.enrichmentData.items.deso.name"), value: matchItem.deso_code },
        { label: t("productPages.valid.enrichmentData.items.regso.name"), value: matchItem.regso_code },
        { label: t("productPages.valid.enrichmentData.items.uuid.name"), value: matchItem.uuid },
        { label: t("productPages.valid.enrichmentData.items.postalCode.name"), value: matchItem.postcode },
        { label: t("productPages.valid.enrichmentData.items.city.name"), value: matchItem.locality },
      ].filter((r) => r.value)
    : [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("productPages.valid.demo.enrich.description")}
      </p>

      <TabQuotaCounter action="enrich" label="Berikningar" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label>{t("productPages.valid.demo.validate.streetLabel")}</Label>
          <Input
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder={country.placeholders.street}
            disabled={rateLimited}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("productPages.valid.demo.validate.postcodeLabel")}</Label>
          <Input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder={country.placeholders.postcode}
            disabled={rateLimited}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("productPages.valid.demo.validate.localityLabel")}</Label>
          <Input
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
            placeholder={country.placeholders.locality}
            disabled={rateLimited}
          />
        </div>
      </div>

      <Button onClick={handleEnrich} disabled={loading || !street || rateLimited}>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <Sparkles className="h-4 w-4 mr-2" />
        )}
        {t("productPages.valid.demo.enrich.button")}
      </Button>

      <AnimatePresence mode="wait">
        {enrichmentRows.length > 0 && (
          <motion.div
            key="enrich-result"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-md border bg-background overflow-hidden"
          >
            <div className="bg-accent/50 px-4 py-2.5 border-b">
              <p className="text-sm font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                {matchItem.street} {matchItem.street_number}, {matchItem.postcode} {matchItem.locality}
              </p>
            </div>
            <div className="divide-y max-h-64 overflow-y-auto">
              {enrichmentRows.map((row, i) => (
                <div key={i} className="flex flex-col sm:grid sm:grid-cols-2 px-4 py-2 text-sm gap-0.5">
                  <span className="text-muted-foreground text-xs sm:text-sm">{row.label}</span>
                  <span className="font-medium break-all">{row.value}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
        {hasError && (
          <motion.div
            key="enrich-error"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 p-4"
          >
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-medium">
              <XCircle className="h-5 w-5" />
              {t("productPages.valid.demo.enrich.errorMessage")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main ValidDemo                                                     */
/* ------------------------------------------------------------------ */

function CountrySelector() {
  const { t } = useTranslation();
  const { country, setCountry } = useCountry();

  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-xs text-muted-foreground">
        {t("productPages.valid.demo.countryLabel")}
      </span>
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {COUNTRIES.map((c) => (
          <button
            key={c.code}
            type="button"
            onClick={() => setCountry(c)}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all ${
              country.code === c.code
                ? "bg-primary text-primary-foreground shadow-sm"
                : "bg-secondary/60 text-muted-foreground hover:bg-secondary"
            }`}
          >
            <span className="text-sm">{c.flag}</span>
            <span className="hidden sm:inline">{t(c.labelKey)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ValidDemo() {
  const { t } = useTranslation();
  const [country, setCountry] = useState<CountryConfig>(COUNTRIES[0]);
  const [suggest, setSuggest] = useState<ActionQuota>(defaultQuota);
  const [validate, setValidate] = useState<ActionQuota>(defaultQuota);
  const [enrich, setEnrich] = useState<ActionQuota>(defaultQuota);
  const [statusLoaded, setStatusLoaded] = useState(false);

  useEffect(() => {
    callValidApi("status", {})
      .then((data) => {
        if (data.suggest) setSuggest(data.suggest);
        if (data.validate) setValidate(data.validate);
        if (data.enrich) setEnrich(data.enrich);
        setStatusLoaded(true);
      })
      .catch(() => {
        setStatusLoaded(true);
      });
  }, []);

  const updateQuota = useCallback((quotas: Record<string, ActionQuota>) => {
    if (quotas.suggest) setSuggest(quotas.suggest);
    if (quotas.validate) setValidate(quotas.validate);
    if (quotas.enrich) setEnrich(quotas.enrich);
  }, []);

  const allExhausted = suggest.remaining <= 0 && validate.remaining <= 0 && enrich.remaining <= 0;

  return (
    <CountryContext.Provider value={{ country, setCountry }}>
      <RateLimitContext.Provider value={{ suggest, validate, enrich, updateQuota }}>
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-display">
              {t("productPages.valid.demo.title")}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("productPages.valid.demo.subtitle")}
            </p>
          </div>

          <div className="w-full max-w-2xl mx-auto space-y-4">
            <CountrySelector />

            <RateLimitBanner />

            <Tabs defaultValue="autocomplete" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="autocomplete" className="text-xs sm:text-sm" disabled={allExhausted}>
                  <Search className="h-3.5 w-3.5 mr-1.5 hidden sm:inline-block" />
                  {t("productPages.valid.demo.tabs.autocomplete")}
                </TabsTrigger>
                <TabsTrigger value="validate" className="text-xs sm:text-sm" disabled={allExhausted}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 hidden sm:inline-block" />
                  {t("productPages.valid.demo.tabs.validate")}
                </TabsTrigger>
                <TabsTrigger value="enrich" className="text-xs sm:text-sm" disabled={allExhausted}>
                  <Sparkles className="h-3.5 w-3.5 mr-1.5 hidden sm:inline-block" />
                  {t("productPages.valid.demo.tabs.enrich")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="autocomplete" className="mt-6">
                <AutocompleteTab />
              </TabsContent>
              <TabsContent value="validate" className="mt-6">
                <ValidateTab />
              </TabsContent>
              <TabsContent value="enrich" className="mt-6">
                <EnrichTab />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </RateLimitContext.Provider>
    </CountryContext.Provider>
  );
}
